import {
    differenceInDays,
    differenceInHours,
    format,
    parse,
    formatDuration,
    Locale,
    addDays,
    startOfDay,
} from 'date-fns'
import { enUS, zhTW, id } from 'date-fns/locale'
import { Country } from './api/constants'
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz'
import { isValid } from 'date-fns'

export const CountryTimezoneMap: Record<Country, string> = {
    SG: 'Asia/Singapore',
    ID: 'Asia/Jakarta',
    PH: 'Asia/Manila',
    TW: 'Asia/Taipei',
    TH: 'Asia/Bangkok',
    VN: 'Asia/Ho_Chi_Minh',
    HK: 'Asia/Hong_Kong',
    MY: 'Asia/Kuala_Lumpur',
}

export const fixBackendDateFormat = (date: string) => {
    return new Date(date).toISOString()
}

export const backendDateToJsDate = (date: string | null, country?: Country) => {
    if (!date || date === null) {
        throw 'Invalid Date'
    }
    if (country) {
        return utcToZonedTime(new Date(date), CountryTimezoneMap[country])
    } else {
        let jsDate = parse(date, "yyyy-MM-dd'T'HH:mm:ss.SSSSSSxxx", new Date())
        if (!isValid(jsDate)) {
            jsDate = parse(date, "yyyy-MM-dd'T'HH:mm:ssX", new Date())
        }
        return jsDate
    }
}

export const jsDateToBackendDate = (date: string | null, country?: Country) => {
    if (!date || date === null) {
        throw 'Invalid Date'
    }
    if (country) {
        return zonedTimeToUtc(new Date(date), CountryTimezoneMap[country])
    } else {
        let jsDate = parse(date, 'd MMM yyyy, h:mm a', new Date())
        if (!isValid(jsDate)) {
            jsDate = parse(date, 'd MMM yyyy', new Date())
        }
        return jsDate
    }
}

interface ParseBackendDateOptions {
    showTime?: boolean
    showYear?: boolean
    addSeconds?: number
}

export const parseBackendDate = (
    date: string,
    country?: Country,
    { showTime = false, showYear = true, addSeconds = 0 }: ParseBackendDateOptions = {},
    customFormat?: string,
): string => {
    try {
        const jsDate = backendDateToJsDate(date, country)
        if (addSeconds < 0) {
            jsDate.setSeconds(jsDate.getSeconds() - Math.abs(addSeconds))
        } else if (addSeconds > 0) {
            jsDate.setSeconds(jsDate.getSeconds() + addSeconds)
        }
        if (showTime) return format(jsDate, 'd MMM yyyy, h:mm a')
        if (customFormat) return format(jsDate, customFormat)
        if (!showYear) return format(jsDate, 'd MMM')
        return format(jsDate, 'd MMM yyyy')
    } catch (err) {
        return date
    }
}

export const parseJsDate = (date: string, showTime = false, showYear = true, addSeconds = 0, addDays = 0) => {
    try {
        const jsDate = parse(date, 'd MMM yyyy', new Date())
        if (addSeconds < 0) {
            jsDate.setSeconds(jsDate.getSeconds() - Math.abs(addSeconds))
        } else if (addSeconds > 0) {
            jsDate.setSeconds(jsDate.getSeconds() + addSeconds)
        }
        if (addDays < 0) {
            jsDate.setDate(jsDate.getDate() - Math.abs(addDays))
        } else if (addDays > 0) {
            jsDate.setDate(jsDate.getDate() + addDays)
        }
        if (showTime) return format(jsDate, 'd MMM yyyy, h:mm a')
        if (!showYear) return format(jsDate, 'd MMMM')
        return format(jsDate, 'd MMM yyyy')
    } catch (err) {
        return date
    }
}

export const formatBackendDate = (date: string, country: Country, dateFormat = 'h:mm a, d MMMM yyyy'): string => {
    try {
        const jsDate = backendDateToJsDate(date, country)
        return format(jsDate, dateFormat)
    } catch (err) {
        return date
    }
}

export const convertToBackendStr = (date: string, country: Country) => {
    try {
        const jsDate = jsDateToBackendDate(date, country)
        return jsDate.toISOString().split('.')[0] + 'Z'
    } catch (err) {
        return date
    }
}

export const addDay = (date: string, days: number) => {
    const dateStr = new Date(date)
    dateStr.setDate(dateStr.getDate() + days)
    return dateStr.toISOString()
}

export const dateIsInPast = (date: string, country: Country, customCurrentDate?: Date) => {
    const jsDate = backendDateToJsDate(date)
    if (customCurrentDate) {
        return customCurrentDate > jsDate
    }
    return getTimeZonedDateNow(country) >= jsDate
}

export const jsDateIsInPast = (date: string, country: Country) => {
    const jsDate = new Date(date)
    return getTimeZonedDateNow(country) >= jsDate
}

export const daysToDate = (date: string, country: Country) => {
    const jsDate = backendDateToJsDate(date)
    return differenceInDays(jsDate, getTimeZonedDateNow(country))
}

export const getLocale = (locale: string): Locale => {
    if (locale.includes('id')) {
        return id
    } else if (locale.includes('zh')) {
        return zhTW
    }

    return enUS
}

export const getDurationInDaysAndHours = (date: string, locale: string, country: Country): string | null => {
    const now = getTimeZonedDateNow(country)
    const dateDate = new Date(date)
    const daysDifference = differenceInDays(dateDate, now)
    if (daysDifference >= 1) {
        const remainHour = addDays(dateDate, -daysDifference)
        const hoursDifference = differenceInHours(remainHour, now)
        return formatDuration(
            { days: daysDifference, hours: hoursDifference },
            { format: ['days', 'hours'], locale: getLocale(locale) },
        )
    } else {
        const hoursDifference = differenceInHours(dateDate, now)
        return hoursDifference > 0
            ? formatDuration({ hours: hoursDifference }, { format: ['hours'], locale: getLocale(locale) })
            : null
    }
}

export const getDurationByDays = (date: string, country: Country): number => {
    const now = getTimeZonedDateNow(country)
    const dateDate = new Date(date)
    return differenceInDays(dateDate, now)
}

export const getTimeZonedDateNow = (country: Country) => {
    return utcToZonedTime(new Date(), CountryTimezoneMap[country])
}

export const getDurationByDaysWithDates = (startDate: string, endDate: string): number => {
    const startDateDate = new Date(startDate)
    const endDateDate = new Date(endDate)

    return differenceInDays(startDateDate, endDateDate)
}

export const getStartOfToday = (country: Country) => {
    return utcToZonedTime(startOfDay(new Date()), CountryTimezoneMap[country])
}
